package fyi.moko.hotpl.ui.detail

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import fyi.moko.hotpl.domain.model.PlaceCategory

/**
 * A grayscale, line-art category illustration used as the detail header when a place
 * has no user photo. Drawn programmatically (no image assets, no network) so it stays
 * offline and weightless, and matches the app's derived-visual language (emotion color,
 * visit glow). [category] is derived from the place's tags via [PlaceCategory.fromTags];
 * null renders a generic location pin.
 *
 * Colors are explicit neutral grays (per light/dark) rather than the purple-tinted
 * theme palette, to keep the fallback truly grayscale and recede behind real photos.
 */
@Composable
fun PlaceCategoryImage(
    category: PlaceCategory?,
    modifier: Modifier = Modifier,
) {
    val dark = isSystemInDarkTheme()
    val top = if (dark) Color(0xFF2B2B2B) else Color(0xFFEDEDED)
    val bottom = if (dark) Color(0xFF212121) else Color(0xFFDFDFDF)
    val ink = if (dark) Color(0xFF7C7C7C) else Color(0xFF8C8C8C)

    Box(
        modifier = modifier.background(Brush.verticalGradient(listOf(top, bottom))),
    ) {
        Canvas(Modifier.fillMaxSize()) {
            // Square draw region centered in the box; glyph occupies ~46% of the min side.
            val half = minOf(size.width, size.height) * 0.23f
            val center = Offset(size.width / 2f, size.height / 2f)
            val stroke = Stroke(
                width = half * 0.16f,
                cap = StrokeCap.Round,
                join = StrokeJoin.Round,
            )
            drawCategoryGlyph(category, center, half, ink, stroke)
        }
    }
}

/** Maps a normalized point (fx, fy in -1..1, y-down) into the centered square region. */
private fun DrawScope.p(center: Offset, half: Float, fx: Float, fy: Float): Offset =
    Offset(center.x + fx * half, center.y + fy * half)

private fun DrawScope.drawCategoryGlyph(
    category: PlaceCategory?,
    c: Offset,
    h: Float,
    ink: Color,
    stroke: Stroke,
) {
    when (category) {
        PlaceCategory.CAFE -> drawCafe(c, h, ink, stroke)
        PlaceCategory.BAKERY -> drawBakery(c, h, ink, stroke)
        PlaceCategory.BAR -> drawBar(c, h, ink, stroke)
        PlaceCategory.RESTAURANT -> drawRestaurant(c, h, ink, stroke)
        PlaceCategory.ARCHITECTURE -> drawArchitecture(c, h, ink, stroke)
        PlaceCategory.PARK -> drawPark(c, h, ink, stroke)
        PlaceCategory.MOUNTAIN -> drawMountain(c, h, ink, stroke)
        PlaceCategory.LAKE -> drawLake(c, h, ink, stroke)
        PlaceCategory.BEACH -> drawBeach(c, h, ink, stroke)
        null -> drawPin(c, h, ink, stroke)
    }
}

private fun DrawScope.line(a: Offset, b: Offset, ink: Color, stroke: Stroke) =
    drawLine(ink, a, b, strokeWidth = stroke.width, cap = stroke.cap)

private fun DrawScope.drawCafe(c: Offset, h: Float, ink: Color, s: Stroke) {
    // Cup body: trapezoid open at the top.
    val body = Path().apply {
        moveTo(p(c, h, -0.55f, -0.1f).x, p(c, h, -0.55f, -0.1f).y)
        lineTo(p(c, h, -0.42f, 0.62f).x, p(c, h, -0.42f, 0.62f).y)
        lineTo(p(c, h, 0.42f, 0.62f).x, p(c, h, 0.42f, 0.62f).y)
        lineTo(p(c, h, 0.55f, -0.1f).x, p(c, h, 0.55f, -0.1f).y)
    }
    drawPath(body, ink, style = s)
    line(p(c, h, -0.55f, -0.1f), p(c, h, 0.55f, -0.1f), ink, s)
    // Handle.
    drawArc(
        color = ink,
        startAngle = -70f,
        sweepAngle = 140f,
        useCenter = false,
        topLeft = Offset(p(c, h, 0.45f, -0.05f).x, p(c, h, 0.45f, -0.05f).y),
        size = Size(h * 0.5f, h * 0.5f),
        style = s,
    )
    // Steam.
    line(p(c, h, -0.2f, -0.75f), p(c, h, -0.2f, -0.35f), ink, s)
    line(p(c, h, 0.15f, -0.8f), p(c, h, 0.15f, -0.4f), ink, s)
}

private fun DrawScope.drawBakery(c: Offset, h: Float, ink: Color, s: Stroke) {
    // Loaf: a rounded dome on a baseline.
    val loaf = Path().apply {
        moveTo(p(c, h, -0.7f, 0.35f).x, p(c, h, -0.7f, 0.35f).y)
        cubicTo(
            p(c, h, -0.7f, -0.5f).x, p(c, h, -0.7f, -0.5f).y,
            p(c, h, 0.7f, -0.5f).x, p(c, h, 0.7f, -0.5f).y,
            p(c, h, 0.7f, 0.35f).x, p(c, h, 0.7f, 0.35f).y,
        )
        close()
    }
    drawPath(loaf, ink, style = s)
    // Score lines on top.
    line(p(c, h, -0.28f, -0.05f), p(c, h, -0.05f, -0.32f), ink, s)
    line(p(c, h, 0.05f, -0.05f), p(c, h, 0.28f, -0.32f), ink, s)
}

private fun DrawScope.drawBar(c: Offset, h: Float, ink: Color, s: Stroke) {
    // Cocktail glass: an inverted triangle bowl on a stem and foot.
    val bowl = Path().apply {
        moveTo(p(c, h, -0.62f, -0.55f).x, p(c, h, -0.62f, -0.55f).y)
        lineTo(p(c, h, 0.62f, -0.55f).x, p(c, h, 0.62f, -0.55f).y)
        lineTo(p(c, h, 0f, 0.1f).x, p(c, h, 0f, 0.1f).y)
        close()
    }
    drawPath(bowl, ink, style = s)
    // Stem and foot.
    line(p(c, h, 0f, 0.1f), p(c, h, 0f, 0.62f), ink, s)
    line(p(c, h, -0.34f, 0.62f), p(c, h, 0.34f, 0.62f), ink, s)
    // Garnish on a pick.
    line(p(c, h, 0.18f, -0.78f), p(c, h, 0.42f, -0.45f), ink, s)
    drawCircle(ink, radius = h * 0.09f, center = p(c, h, 0.16f, -0.8f), style = s)
}

private fun DrawScope.drawRestaurant(c: Offset, h: Float, ink: Color, s: Stroke) {
    // Fork (left).
    line(p(c, h, -0.35f, -0.1f), p(c, h, -0.35f, 0.7f), ink, s)
    line(p(c, h, -0.55f, -0.7f), p(c, h, -0.55f, -0.15f), ink, s)
    line(p(c, h, -0.35f, -0.7f), p(c, h, -0.35f, -0.15f), ink, s)
    line(p(c, h, -0.15f, -0.7f), p(c, h, -0.15f, -0.15f), ink, s)
    line(p(c, h, -0.55f, -0.15f), p(c, h, -0.15f, -0.15f), ink, s)
    // Knife (right): handle line + a blade.
    line(p(c, h, 0.4f, 0.05f), p(c, h, 0.4f, 0.7f), ink, s)
    val blade = Path().apply {
        moveTo(p(c, h, 0.4f, 0.05f).x, p(c, h, 0.4f, 0.05f).y)
        lineTo(p(c, h, 0.4f, -0.7f).x, p(c, h, 0.4f, -0.7f).y)
        cubicTo(
            p(c, h, 0.62f, -0.4f).x, p(c, h, 0.62f, -0.4f).y,
            p(c, h, 0.62f, -0.1f).x, p(c, h, 0.62f, -0.1f).y,
            p(c, h, 0.4f, 0.05f).x, p(c, h, 0.4f, 0.05f).y,
        )
        close()
    }
    drawPath(blade, ink, style = s)
}

private fun DrawScope.drawArchitecture(c: Offset, h: Float, ink: Color, s: Stroke) {
    val base = 0.7f
    // Three towers of varying heights on a shared baseline.
    fun tower(x0: Float, x1: Float, topY: Float) {
        val path = Path().apply {
            moveTo(p(c, h, x0, base).x, p(c, h, x0, base).y)
            lineTo(p(c, h, x0, topY).x, p(c, h, x0, topY).y)
            lineTo(p(c, h, x1, topY).x, p(c, h, x1, topY).y)
            lineTo(p(c, h, x1, base).x, p(c, h, x1, base).y)
        }
        drawPath(path, ink, style = s)
    }
    tower(-0.75f, -0.25f, -0.1f)
    tower(-0.2f, 0.2f, -0.7f)
    tower(0.25f, 0.75f, 0.15f)
    line(p(c, h, -0.85f, base), p(c, h, 0.85f, base), ink, s)
}

private fun DrawScope.drawPark(c: Offset, h: Float, ink: Color, s: Stroke) {
    // Trunk.
    line(p(c, h, 0f, 0.15f), p(c, h, 0f, 0.75f), ink, s)
    // Canopy: three overlapping circles.
    drawCircle(ink, radius = h * 0.34f, center = p(c, h, 0f, -0.35f), style = s)
    drawCircle(ink, radius = h * 0.28f, center = p(c, h, -0.38f, -0.05f), style = s)
    drawCircle(ink, radius = h * 0.28f, center = p(c, h, 0.38f, -0.05f), style = s)
}

private fun DrawScope.drawMountain(c: Offset, h: Float, ink: Color, s: Stroke) {
    // Sun.
    drawCircle(ink, radius = h * 0.16f, center = p(c, h, 0.55f, -0.55f), style = s)
    // Two peaks.
    val peaks = Path().apply {
        moveTo(p(c, h, -0.85f, 0.6f).x, p(c, h, -0.85f, 0.6f).y)
        lineTo(p(c, h, -0.25f, -0.55f).x, p(c, h, -0.25f, -0.55f).y)
        lineTo(p(c, h, 0.1f, 0.1f).x, p(c, h, 0.1f, 0.1f).y)
        lineTo(p(c, h, 0.4f, -0.25f).x, p(c, h, 0.4f, -0.25f).y)
        lineTo(p(c, h, 0.85f, 0.6f).x, p(c, h, 0.85f, 0.6f).y)
    }
    drawPath(peaks, ink, style = s)
    line(p(c, h, -0.85f, 0.6f), p(c, h, 0.85f, 0.6f), ink, s)
}

private fun DrawScope.drawLake(c: Offset, h: Float, ink: Color, s: Stroke) {
    // A distant peak.
    val peak = Path().apply {
        moveTo(p(c, h, -0.55f, -0.05f).x, p(c, h, -0.55f, -0.05f).y)
        lineTo(p(c, h, -0.1f, -0.65f).x, p(c, h, -0.1f, -0.65f).y)
        lineTo(p(c, h, 0.4f, -0.05f).x, p(c, h, 0.4f, -0.05f).y)
    }
    drawPath(peak, ink, style = s)
    // Water lines.
    wave(c, h, 0.25f, ink, s)
    wave(c, h, 0.5f, ink, s)
    wave(c, h, 0.75f, ink, s)
}

private fun DrawScope.drawBeach(c: Offset, h: Float, ink: Color, s: Stroke) {
    // Sun.
    drawCircle(ink, radius = h * 0.3f, center = p(c, h, 0f, -0.35f), style = s)
    // Waves.
    wave(c, h, 0.35f, ink, s)
    wave(c, h, 0.62f, ink, s)
}

/** A single horizontal wavy line at normalized [y]. */
private fun DrawScope.wave(c: Offset, h: Float, y: Float, ink: Color, s: Stroke) {
    val path = Path().apply {
        moveTo(p(c, h, -0.8f, y).x, p(c, h, -0.8f, y).y)
        cubicTo(
            p(c, h, -0.5f, y - 0.18f).x, p(c, h, -0.5f, y - 0.18f).y,
            p(c, h, -0.3f, y + 0.18f).x, p(c, h, -0.3f, y + 0.18f).y,
            p(c, h, 0f, y).x, p(c, h, 0f, y).y,
        )
        cubicTo(
            p(c, h, 0.3f, y - 0.18f).x, p(c, h, 0.3f, y - 0.18f).y,
            p(c, h, 0.5f, y + 0.18f).x, p(c, h, 0.5f, y + 0.18f).y,
            p(c, h, 0.8f, y).x, p(c, h, 0.8f, y).y,
        )
    }
    drawPath(path, ink, style = s)
}

private fun DrawScope.drawPin(c: Offset, h: Float, ink: Color, s: Stroke) {
    // Teardrop location pin.
    val pin = Path().apply {
        moveTo(p(c, h, 0f, 0.75f).x, p(c, h, 0f, 0.75f).y)
        cubicTo(
            p(c, h, -0.75f, -0.1f).x, p(c, h, -0.75f, -0.1f).y,
            p(c, h, -0.55f, -0.75f).x, p(c, h, -0.55f, -0.75f).y,
            p(c, h, 0f, -0.75f).x, p(c, h, 0f, -0.75f).y,
        )
        cubicTo(
            p(c, h, 0.55f, -0.75f).x, p(c, h, 0.55f, -0.75f).y,
            p(c, h, 0.75f, -0.1f).x, p(c, h, 0.75f, -0.1f).y,
            p(c, h, 0f, 0.75f).x, p(c, h, 0f, 0.75f).y,
        )
        close()
    }
    drawPath(pin, ink, style = s)
    drawCircle(ink, radius = h * 0.2f, center = p(c, h, 0f, -0.35f), style = s)
}
